import { useState, useEffect, useCallback } from 'react';
import { Collapse, Row, Col, DatePicker, Avatar, Input, Tooltip, Popover } from 'antd';
import { FilterOutlined, CloseCircleFilled, SearchOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import styles from './AdvancedFilterPanel.module.scss';

const { RangePicker } = DatePicker;
const MAX_VISIBLE = 5;

const ROLE_OPTIONS = [
  { value: null, label: 'Tous' },
  { value: 'assigned_by_me', label: "Que j'ai assigné" },
  { value: 'responsable', label: 'Dont je suis responsable' },
  { value: 'assigned_to_me', label: "Qu'on m'a assigné" },
  { value: 'collaborateur', label: 'Simple collaborateur' },
];

const AdvancedFilterPanel = ({
  filters,
  onFilterChange,
  getResponsables,
  extraFilters,
  searchPlaceholder,
  onSearch,
  searchValue,
  showRoleFilter,
  roleFilterValue,
  onRoleFilterChange,
  roleOptions,
}) => {
  const [responsables, setResponsables] = useState([]);
  const [loadingResponsables, setLoadingResponsables] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [avatarSearch, setAvatarSearch] = useState('');

  const loadResponsables = useCallback(async () => {
    if (!getResponsables) return;
    setLoadingResponsables(true);
    // Only send filters the responsables endpoint actually understands
    const safeFilters = {
      searchText:    filters.searchText    ?? undefined,
      date_from:     filters.date_from     ?? undefined,
      date_to:       filters.date_to       ?? undefined,
      created_from:  filters.created_from  ?? undefined,
      created_to:    filters.created_to    ?? undefined,
      departement_id:filters.departement_id ?? undefined,
      affaire_id:    filters.affaire_id    ?? undefined,
      statut_id:     filters.statut_id     ?? undefined,
      priorite_id:   filters.priorite_id   ?? undefined,
      my_role:       roleFilterValue       ?? undefined,
    };
    try {
      const res = await getResponsables(safeFilters);
      setResponsables(res.data?.data || res.data || []);
    } catch {
      setResponsables([]);
    } finally {
      setLoadingResponsables(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.searchText, filters.date_from, filters.date_to,
    filters.created_from, filters.created_to, filters.departement_id,
    filters.affaire_id, filters.statut_id, filters.priorite_id,
    roleFilterValue,
  ]);

  useEffect(() => { loadResponsables(); }, [loadResponsables]);

  const handleDateRange = (dates) => {
    onFilterChange('date_from', dates?.[0]?.format('YYYY-MM-DD') ?? null);
    onFilterChange('date_to', dates?.[1]?.format('YYYY-MM-DD') ?? null);
  };

  const dateRangeValue = filters.date_from && filters.date_to
    ? [dayjs(filters.date_from), dayjs(filters.date_to)]
    : null;

  const toggleResponsable = (id) =>
    onFilterChange('responsable_id', filters.responsable_id === id ? null : id);

  const visible  = responsables.slice(0, MAX_VISIBLE);
  const overflow = responsables.length - MAX_VISIBLE;
  const filtered = responsables.filter(u =>
    !avatarSearch || u.name?.toLowerCase().includes(avatarSearch.toLowerCase())
  );

  const popoverContent = (
    <div className={styles.popoverList}>
      <Input
        prefix={<SearchOutlined />}
        placeholder="Rechercher..."
        value={avatarSearch}
        onChange={e => setAvatarSearch(e.target.value)}
        size="small"
        style={{ marginBottom: 8 }}
        allowClear
      />
      {filtered.map(u => (
        <div
          key={u.id}
          className={`${styles.popoverItem} ${filters.responsable_id === u.id ? styles.popoverItemActive : ''}`}
          onClick={() => { toggleResponsable(u.id); setPopoverOpen(false); }}
        >
          <Avatar size={26} src={u.avatar}>{u.name?.[0]}</Avatar>
          <span className={styles.popoverName}>{u.name}</span>
          {filters.responsable_id === u.id && <CloseCircleFilled className={styles.popoverCheck} />}
        </div>
      ))}
      {filtered.length === 0 && <div className={styles.popoverEmpty}>Aucun résultat</div>}
    </div>
  );

  return (
    <div className={styles.wrapper}>
      {/* ── Role filter bar ────────────────────────────────────── */}
      {showRoleFilter && (
        <div className={styles.roleBar}>
          {(roleOptions ?? ROLE_OPTIONS).map(opt => (
            <button
              key={String(opt.value)}
              className={`${styles.roleBtn} ${roleFilterValue === opt.value ? styles.roleBtnActive : ''}`}
              onClick={() => onRoleFilterChange(opt.value)}
              type="button"
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Filtres avancés (Collapse) ─────────────────────────── */}
      <Collapse
        defaultActiveKey={[]}
        ghost={false}
        style={{ borderBottom: '1px solid var(--border-default, #E8EAED)' }}
        items={[{
          key: 'filters',
          label: (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500, fontSize: 13 }}>
              <FilterOutlined />
              Filtres avancés
            </span>
          ),
          children: (
            <Row gutter={[12, 12]} style={{ padding: '4px 0 8px' }}>
              <Col xs={24} sm={12} lg={8}>
                <div className={styles.filterField}>
                  <div className={styles.fieldLabel}>Intervalle de date limite</div>
                  <RangePicker
                    style={{ width: '100%' }}
                    value={dateRangeValue}
                    onChange={handleDateRange}
                    format="DD/MM/YYYY"
                    placeholder={['Du', 'Au']}
                    allowClear
                  />
                </div>
              </Col>
              {extraFilters}
            </Row>
          ),
        }]}
      />

      {/* ── Barre recherche + avatars responsables ─────────────── */}
      <div className={styles.searchBar}>
        <Input.Search
          value={searchValue}
          onSearch={onSearch}
          onChange={e => onSearch?.(e.target.value)}
          placeholder={searchPlaceholder || 'Rechercher...'}
          className={styles.searchInput}
          allowClear
        />

        {getResponsables && responsables.length > 0 && (
          <div className={styles.avatarGroup}>
            <Tooltip title="Tous">
              <Avatar
                size={32}
                className={`${styles.avatar} ${!filters.responsable_id ? styles.avatarActive : ''}`}
                onClick={() => onFilterChange('responsable_id', null)}
              >
                <FilterOutlined style={{ fontSize: 13 }} />
              </Avatar>
            </Tooltip>

            {visible.map(u => (
              <Tooltip key={u.id} title={u.name}>
                <Avatar
                  size={32}
                  src={u.avatar}
                  className={`${styles.avatar} ${filters.responsable_id === u.id ? styles.avatarActive : ''}`}
                  onClick={() => toggleResponsable(u.id)}
                >
                  {u.name?.[0]}
                </Avatar>
              </Tooltip>
            ))}

            {overflow > 0 && (
              <Popover
                content={popoverContent}
                title="Filtrer par responsable"
                trigger="click"
                open={popoverOpen}
                onOpenChange={setPopoverOpen}
                placement="bottomLeft"
              >
                <Avatar size={32} className={`${styles.avatar} ${styles.avatarMore}`}>
                  +{overflow}
                </Avatar>
              </Popover>
            )}
          </div>
        )}

        {loadingResponsables && (
          <span style={{ fontSize: 12, color: 'var(--text-disabled, #8C9BAB)' }}>Chargement…</span>
        )}
      </div>
    </div>
  );
};

AdvancedFilterPanel.propTypes = {
  filters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  getResponsables: PropTypes.func,
  extraFilters: PropTypes.node,
  searchPlaceholder: PropTypes.string,
  onSearch: PropTypes.func,
  searchValue: PropTypes.string,
  showRoleFilter: PropTypes.bool,
  roleFilterValue: PropTypes.string,
  onRoleFilterChange: PropTypes.func,
  roleOptions: PropTypes.array,
};

export default AdvancedFilterPanel;
